<template>
  <ModalComponent
    ref="modalRef"
    size="xl"
    :submit-button-text="$t('common.save')"
    :hide-submit-button="true"
    @close="handleClose"
  >
    <template #title>
      {{ $t("company_orders.edit_order_title") }}
    </template>

    <template #body>
      <LoadingIndicatorComponent v-if="isLoading" />

      <div v-else class="edit-company-order">
        <!-- SEND: read-only recipient summary -->
        <div v-if="isSend" class="card mb-3">
          <div class="card-header bg-light py-2">
            <h6 class="mb-0">
              {{ $t("company_orders.send.recipient_information") }}
            </h6>
          </div>
          <div class="card-body py-3">
            <div class="row g-2">
              <div class="col-md-6">
                <div class="text-muted small mb-1">
                  {{ $t("company_orders.send.company_name") }}
                </div>
                <div class="fw-semibold">
                  {{ sendDetails?.recipient_company_name ?? "-" }}
                </div>
              </div>
              <div class="col-md-6">
                <div class="text-muted small mb-1">
                  {{ $t("company_orders.send.registry_country") }}
                </div>
                <div class="fw-semibold">
                  {{ sendDetails?.recipient_registry_country ?? "-" }}
                </div>
              </div>
              <div class="col-md-6">
                <div class="text-muted small mb-1">
                  {{ $t("company_orders.send.eecs_account_id") }}
                </div>
                <div class="font-monospace">
                  {{ sendDetails?.recipient_eecs_account_number ?? "-" }}
                </div>
              </div>
              <div
                v-if="sendDetails?.recipient_registry_internal_account_number"
                class="col-md-6"
              >
                <div class="text-muted small mb-1">
                  {{ $t("company_orders.send.internal_account_id") }}
                </div>
                <div class="font-monospace">
                  {{ sendDetails.recipient_registry_internal_account_number }}
                </div>
              </div>
              <div v-if="sendDetails?.unit_price_pln" class="col-md-6">
                <div class="text-muted small mb-1">
                  {{ $t("company_orders.send.unit_price_pln") }}
                </div>
                <div class="fw-semibold">
                  {{ sendDetails.unit_price_pln }} PLN/MWh
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- CANCEL: statements editor -->
        <CancelStatementsEditor
          v-if="isCancel"
          ref="editorRef"
          :initialRows="initialCancelRows"
          :initialSharedDefaults="initialSharedDefaults"
          :initialGlobalOperationNotes="initialGlobalOperationNotes"
          :selectedRegions="selectedRegions"
          :companyId="props.order.company_id"
          mode="edit-mwh"
        />

        <!-- Portfolio items table -->
        <h4 class="mb-2">
          {{
            isSend
              ? $t("company_orders.send.portfolio_items_title")
              : $t("go_cancel.portfolio_items_title")
          }}
        </h4>
        <PortfolioItemsTable
          ref="portfolioTableRef"
          :selection="selection"
          :enable-count-editing="true"
          :is-for-admin="true"
          :items="editableItems"
          :hide-action-bar="true"
          storage-context="edit-order"
        />

        <div
          v-if="mwhMismatchWarning"
          class="alert alert-warning mt-3"
          role="alert"
        >
          {{ mwhMismatchWarning }}
        </div>

        <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
          {{ errorMessage }}
        </div>
      </div>
    </template>

    <template #footer>
      <button
        type="button"
        class="btn btn-secondary"
        @click="handleClose"
        :disabled="isSaving"
      >
        {{ $t("common.cancel") }}
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!canSave || isSaving"
        @click="handleSave"
      >
        <span
          v-if="isSaving"
          class="spinner-border spinner-border-sm me-1"
          role="status"
          aria-hidden="true"
        ></span>
        {{ $t("common.save") }}
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { ANY_AIB_REGISTRY_SENTINEL } from "@/constants";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import CancelStatementsEditor from "@/dashboard/inventory_management/cancel/CancelStatementsEditor.vue";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import {
  allItemsSelectable,
  isItemAvailable,
} from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import { useCompanyOrderStore } from "@/dashboard/inventory_management/common/useCompanyOrderStore";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import { usePortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import {
  type CancelSharedDefaults,
  getDefaultCancelPeriodRange,
  getDefaultOperationNotesTemplate,
  isStatementPrivate,
} from "@/dashboard/inventory_management/common/cancelStatementHelpers";
import { BeneficiaryTypeEnum } from "@/types";
import type {
  CancelOrderDetails,
  CompanyOrder,
  CompanyOrderUpdatePayload,
  SendOrderDetails,
} from "@/dashboard/inventory_management/common/companyOrderTypes";
import { OrderType } from "@/dashboard/inventory_management/common/companyOrderTypes";
import { companyOrderApi } from "@/dashboard/inventory_management/common/companyOrderApi";
import { computed, nextTick, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
  order: CompanyOrder;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
  "order:updated": [orderId: number];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const portfolioItemsStore = usePortfolioItemsStore();
const selection = usePortfolioSelection({
  policy: allItemsSelectable,
});
const companyOrderStore = useCompanyOrderStore();

const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");
const editorRef =
  useTemplateRef<InstanceType<typeof CancelStatementsEditor>>("editorRef");
const portfolioTableRef =
  useTemplateRef<InstanceType<typeof PortfolioItemsTable>>("portfolioTableRef");

const isLoading = ref(true);
const isSaving = ref(false);
const errorMessage = ref<string | null>(null);
const sendDetails = ref<SendOrderDetails | null>(null);
const initialCancelRows = ref<any[]>([]);
const initialSharedDefaults = ref<CancelSharedDefaults | undefined>(undefined);
const initialGlobalOperationNotes = ref<string | null>(null);

const isSend = computed(() => props.order.order_type === OrderType.SEND);
const isCancel = computed(() => props.order.order_type === OrderType.CANCEL);

// Backend _fetch_and_validate_available_items only accepts items from HOLD or
// VIRTUAL portfolios, plus items from the PPA portfolio that generated the
// order (its source portfolio). Mirror that here so the modal cannot offer
// items the save would reject. Unattached items in a PPA portfolio carry
// status RESERVED (not AVAILABLE) — the status cascade has already excluded
// cancelled/expired/own-use items and flips to PENDING once on an order.
const ADDABLE_PORTFOLIO_TYPES = new Set(["HOLD", "VIRTUAL"]);

const sourcePortfolioId = ref<number | null>(null);

const editableItems = computed(() =>
  portfolioItemsStore.allItems.filter(
    (item) =>
      item.company_order_id === props.order.id ||
      (isItemAvailable(item) &&
        ADDABLE_PORTFOLIO_TYPES.has(item.portfolio_type)) ||
      (item.portfolio_id === sourcePortfolioId.value &&
        item.status === "RESERVED")
  )
);

const selectedRegions = computed(() => {
  const regions = new Set<string>();
  selection.selectedItems.forEach((item) => {
    if (item.registry_region) {
      regions.add(item.registry_region);
    }
  });
  return Array.from(regions);
});

const totalMwhInTable = computed(() => selection.totalSelectedMwh);

const canSave = computed(() => {
  if (selection.selectedItems.length === 0) return false;
  if (isCancel.value) {
    if (!editorRef.value) return false;
    if (!editorRef.value.isValid) return false;
    if (editorRef.value.totalMwhToCancel !== totalMwhInTable.value)
      return false;
  }
  return true;
});

const mwhMismatchWarning = computed(() => {
  if (!isCancel.value || !editorRef.value) return null;
  if (selection.selectedItems.length === 0) return null;
  const statementsMwh = editorRef.value.totalMwhToCancel;
  const portfolioMwh = totalMwhInTable.value;
  if (statementsMwh === portfolioMwh) return null;
  return t("company_orders.mwh_mismatch_warning", {
    statementsMwh,
    portfolioMwh,
  });
});

const loadDetails = async () => {
  try {
    isLoading.value = true;
    const response = await companyOrderApi.getOrderDetails(props.order.id);
    sourcePortfolioId.value = response.data.source_portfolio_id ?? null;
    const details = response.data.order_details;
    if (isSend.value) {
      sendDetails.value = details as SendOrderDetails;
    } else if (isCancel.value) {
      const cd = details as CancelOrderDetails;

      const firstStmt = cd.statements?.[0];
      if (firstStmt) {
        const beneficiaryType =
          (firstStmt.beneficiary_type as BeneficiaryTypeEnum) ??
          BeneficiaryTypeEnum.CONSUMER;
        initialSharedDefaults.value = {
          beneficiary_type: beneficiaryType,
          period_start:
            firstStmt.period_start ?? getDefaultCancelPeriodRange().start,
          period_end: firstStmt.period_end ?? getDefaultCancelPeriodRange().end,
          cancellation_purpose_template:
            getDefaultOperationNotesTemplate(beneficiaryType),
        };
      }

      initialGlobalOperationNotes.value = cd.global_operation_notes ?? null;

      initialCancelRows.value = (cd.statements ?? []).map((stmt, idx) => ({
        temp_id: stmt.temp_id ?? `existing-${idx}`,
        beneficiary_name: stmt.beneficiary_name ?? "",
        // DEV-576: reconstruct the Any AIB dropdown selection from the marker so the (disabled)
        // dropdown shows "Any AIB" rather than "Norway". Display-only — edit-mwh materialization
        // spreads original_statement verbatim (region "NO", is_any_aib true), so the submitted
        // payload is unchanged.
        registry_country_region: stmt.is_any_aib
          ? ANY_AIB_REGISTRY_SENTINEL
          : (stmt.registry_country_region ?? ""),
        registry_code: stmt.registry_code ?? null,
        address: stmt.address ?? null,
        mwh_cancelled: stmt.mwh_cancelled ?? null,
        // DEV-583: privacy is the one statement field still editable in the confirm route, so it is
        // hydrated as row state rather than left inside original_statement.
        keep_statement_private: isStatementPrivate(stmt.grexel_options),
        touched: false,
        original_statement: stmt,
      }));
    }
  } finally {
    isLoading.value = false;
  }
};

const preselectOrderItems = () => {
  const orderItems = portfolioItemsStore.allItems.filter(
    (item) => item.company_order_id === props.order.id
  );
  selection.clearSelection();
  for (const item of orderItems) {
    selection.forceSelectItem(item.id);
  }
};

const handleClose = () => {
  if (isSaving.value) return;
  selection.clearSelection();
  emit("close");
};

const handleSave = async () => {
  if (!canSave.value || isSaving.value) return;
  isSaving.value = true;
  errorMessage.value = null;

  let payload: CompanyOrderUpdatePayload;
  if (isSend.value) {
    if (!sendDetails.value) {
      isSaving.value = false;
      return;
    }
    payload = {
      recipient_eecs_account_number:
        sendDetails.value.recipient_eecs_account_number ?? null,
      recipient_registry_country:
        sendDetails.value.recipient_registry_country ?? null,
      recipient_company_id: sendDetails.value.recipient_company_id ?? null,
      recipient_company_name: sendDetails.value.recipient_company_name,
      recipient_registry_internal_account_number:
        sendDetails.value.recipient_registry_internal_account_number ?? null,
      unit_price_pln: sendDetails.value.unit_price_pln ?? null,
      portfolio_items: selection.selectedItemsForApi,
    };
  } else {
    if (!editorRef.value) {
      isSaving.value = false;
      return;
    }
    payload = {
      statements: editorRef.value.materializedStatements,
      portfolio_items: selection.selectedItemsForApi,
      global_operation_notes: editorRef.value.globalOperationNotes,
      has_region_mismatch: editorRef.value.hasRegionMismatch,
    };
  }

  try {
    await companyOrderStore.updateOrder(props.order.id, payload);
    globalSuccessHandler(
      t("common.success"),
      t("company_orders.edit_order_success")
    );
    emit("order:updated", props.order.id);
    selection.clearSelection();
  } catch (error: any) {
    errorMessage.value = t("company_orders.edit_order_error");
    globalErrorHandler(error);
  } finally {
    isSaving.value = false;
  }
};

onMounted(async () => {
  try {
    await portfolioItemsStore.fetchItems(props.order.company_id);
    await loadDetails();
    await nextTick();
    preselectOrderItems();
  } catch (error) {
    errorMessage.value = t("company_orders.edit_order_load_error");
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
    if (modalRef.value) {
      modalRef.value.showModal = true;
    }
  }
});
</script>

<style scoped lang="scss">
.edit-company-order {
  min-height: 400px;
}
</style>
